import Tab from '@mui/material/Tab';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { TAB_INDEX } from '../pages';
import { Box, Grid } from '@mui/material';

interface CarcinogenicReportTabProps {
    label: string;
    value: TAB_INDEX;
    onlyVisual: boolean;
    active?: boolean;
    complete?: boolean;
    disabled?: boolean;
    /** Correcciones pendientes que caen en este paso (ver `pasoDeCorreccion`). */
    correcciones?: number;
}

const ROJO_CORRECCION = '#B42318';
const GRIS_BLOQUEADO = '#6B777C';

const circulo = {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700
};

/**
 * Indicador del paso: candado si está bloqueado, ✓ si está completo y el
 * número del paso si no. El número reemplaza al "!" amarillo de antes, que
 * marcaba igual un paso sin empezar que uno a medio cargar.
 */
const IndicadorPaso = ({
    numero,
    active,
    complete,
    disabled
}: {
    numero: number;
    active: boolean;
    complete: boolean;
    disabled: boolean;
}) => {
    if (disabled) {
        return (
            <LockOutlinedIcon
                data-testid={`paso-${numero}-bloqueado`}
                sx={{ fontSize: 20, color: GRIS_BLOQUEADO }}
            />
        );
    }
    if (complete) {
        return (
            <Box
                data-testid={`paso-${numero}-completo`}
                sx={{ ...circulo, backgroundColor: 'primary.main', color: 'white' }}
            >
                <CheckIcon sx={{ fontSize: 16 }} />
            </Box>
        );
    }
    return (
        <Box
            sx={{
                ...circulo,
                ...(active
                    ? { backgroundColor: 'primary.main', color: 'white' }
                    : { border: '1.5px solid #5F6B70', color: '#3D4A50' })
            }}
        >
            {numero}
        </Box>
    );
};

const CarcinogenicReportTab = (props: CarcinogenicReportTabProps) => {
    // `active`, `complete`, `onlyVisual` y `correcciones` son de este wrapper:
    // si llegan al Tab de MUI terminan en el DOM ("unknown event handler
    // onlyVisual"). El resto son los props que Tabs inyecta a sus hijos y sí
    // tienen que pasar.
    const {
        label,
        value,
        active = false,
        complete = false,
        disabled = false,
        onlyVisual,
        correcciones = 0,
        ...propsDelTab
    } = props;
    const numero = value + 1;

    return (
        <Grid container alignItems={'center'} sx={{ width: 'auto', flex: '1 1 auto' }}>
            <Tab
                {...propsDelTab}
                disabled={disabled}
                sx={{
                    flex: 1,
                    minHeight: 44,
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    color: '#3D4A50',
                    fontWeight: active ? 700 : 500,
                    transition: 'background-color .15s',
                    bgcolor: active ? '#E8F3E6' : 'transparent',
                    '&:hover': { bgcolor: active ? '#E8F3E6' : '#F3F8F8' },
                    '&.Mui-selected': { color: 'primary.main' },
                    '&.Mui-disabled': { color: GRIS_BLOQUEADO }
                }}
                label={
                    <Box display="flex" alignItems="center" gap={1}>
                        {!onlyVisual && (
                            <IndicadorPaso
                                numero={numero}
                                active={active}
                                complete={complete}
                                disabled={disabled}
                            />
                        )}
                        <span>{label}</span>
                        {correcciones > 0 && (
                            <Box
                                component="span"
                                data-testid={`paso-${numero}-correcciones`}
                                aria-label={`${correcciones} ${
                                    correcciones === 1 ? 'corrección' : 'correcciones'
                                }`}
                                sx={{
                                    minWidth: '20px',
                                    height: '20px',
                                    paddingX: '6px',
                                    borderRadius: '10px',
                                    backgroundColor: ROJO_CORRECCION,
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    lineHeight: '20px'
                                }}
                            >
                                {correcciones}
                            </Box>
                        )}
                    </Box>
                }
                value={value}
            />
        </Grid>
    );
};

export { CarcinogenicReportTab };
